import React from "react";
import LgvDateField from "../../../components/LgvDateField";
import LgvCountryField from "../../../components/LgvCountryField";
import LgvRadioGroup from "../../../components/LgvRadioGroup";
import LgvSelectField from "../../../components/LgvSelectField";
import LgvTextField from "../../../components/LgvTextField";
import { normalizeCompositeRadioOptions } from "../../../utils/radioOptions";
import { normalizeCompositeSelectOptions } from "../../../utils/selectOptions";
import type { CompositeFieldDef } from "../../../utils/compositeValidation";
import {
  getCompositeSubFieldDateBounds,
  inferCompositeSubFieldFormat,
  getCompositeSubFieldInputType,
} from "../../../utils/compositeFieldUtils";
import { extractPhoneDigits } from "../../../utils/usPhoneValidation";

type Props = {
  field: CompositeFieldDef;
  fieldId: string;
  fieldValue: unknown;
  readOnly?: boolean;
  controlClassName?: string;
  onChange: (value: unknown) => void;
};

export function CompositeSubFieldControl({
  field,
  fieldId,
  fieldValue,
  readOnly = false,
  controlClassName,
  onChange,
}: Props) {
  const fieldType = field.type?.toLowerCase() || "text";
  const stringValue = String(fieldValue ?? "");
  const dateBounds =
    fieldType === "date" ? getCompositeSubFieldDateBounds(field) : {};
  const inputType = getCompositeSubFieldInputType(field);
  const validationFormat = inferCompositeSubFieldFormat(field);
  const isCountryField =
    fieldType === "country" ||
    /\bcountry\b/i.test(`${field.key ?? ""} ${field.label ?? ""}`);

  if (isCountryField) {
    return (
      <LgvCountryField
        id={fieldId}
        value={fieldValue}
        disabled={readOnly}
        required={field.required}
        placeholder={
          (field as { placeholder?: string }).placeholder || "Select country..."
        }
        className={controlClassName}
        onChange={(country) => onChange(country?.name ?? "")}
      />
    );
  }

  if (fieldType === "select" || fieldType === "dropdown") {
    return (
      <LgvSelectField
        id={fieldId}
        value={stringValue}
        options={normalizeCompositeSelectOptions(field.options)}
        disabled={readOnly}
        required={field.required}
        placeholder="Select..."
        className={controlClassName}
        onChange={onChange}
      />
    );
  }

  if (fieldType === "radio") {
    return (
      <LgvRadioGroup
        name={fieldId}
        value={fieldValue}
        options={normalizeCompositeRadioOptions(field.options)}
        disabled={readOnly}
        required={field.required}
        className={controlClassName}
        onChange={onChange}
      />
    );
  }

  if (fieldType === "textarea" || fieldType === "long_text") {
    return (
      <LgvTextField
        id={fieldId}
        value={stringValue}
        multiline
        disabled={readOnly}
        required={field.required}
        placeholder={(field as { placeholder?: string }).placeholder}
        className={controlClassName}
        onChange={onChange}
      />
    );
  }

  if (fieldType === "number") {
    return (
      <LgvTextField
        id={fieldId}
        type="number"
        value={stringValue}
        disabled={readOnly}
        required={field.required}
        min={field.min}
        max={field.max}
        className={controlClassName}
        onChange={(value) =>
          onChange(value === "" ? "" : Number(value))
        }
      />
    );
  }

  if (fieldType === "date") {
    return (
      <LgvDateField
        id={fieldId}
        value={stringValue}
        min={dateBounds.min}
        max={dateBounds.max}
        disabled={readOnly}
        required={field.required}
        placeholder={(field as { placeholder?: string }).placeholder}
        className={controlClassName}
        onChange={onChange}
      />
    );
  }

  return (
    <LgvTextField
      id={fieldId}
      type={inputType === "email" ? "email" : "text"}
      value={stringValue}
      disabled={readOnly}
      required={field.required}
      min={dateBounds.min}
      max={dateBounds.max}
      placeholder={(field as { placeholder?: string }).placeholder}
      className={controlClassName}
      inputMode={validationFormat === "us_phone" ? "numeric" : undefined}
      maxLength={validationFormat === "us_phone" ? 10 : undefined}
      autoComplete={validationFormat === "us_phone" ? "tel" : undefined}
      onChange={(value) => {
        if (validationFormat === "us_phone") {
          onChange(extractPhoneDigits(value).slice(0, 10));
          return;
        }
        onChange(value);
      }}
    />
  );
}
